import React, { useEffect, useState } from 'react';

import { Input, InputAffix, InputRoot } from '@/design-system/input';
import { cn } from '@/design-system/lib/utils';

/**
 * KmInput (DD8) — kilometres as a canonical decimal STRING with a dot
 * (`"84.6"`), max 2 dp. Ops type with a comma or a dot; the display keeps
 * their separator while the emitted value is always dot-normalised so the
 * payload's `Number()` is exact. Suffix "km", `inputMode="decimal"`.
 *
 * Empty stays empty (`""`) — the parent decides whether that is an error.
 */
export interface KmInputProps {
  id?: string;
  /** Canonical decimal string (`"84.6"`), or `""`. */
  value: string;
  onChange: (km: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  invalid?: boolean;
  locked?: boolean;
  placeholder?: string;
  'aria-label'?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const PARTIAL = /^\d{0,6}([.,]\d{0,2})?$/;

/** `"84,60"` → `"84.6"`; `"84,"` → `"84"`; invalid → null. */
export const normalizeKm = (raw: string): string | null => {
  const s = raw.trim().replace(',', '.');
  if (s === '' || s === '.') return '';
  if (!/^\d{0,6}(\.\d{0,2})?$/.test(s)) return null;
  const n = Number(s.endsWith('.') ? s.slice(0, -1) : s);
  if (!Number.isFinite(n)) return null;
  // Round to 2 dp and strip trailing zeros.
  return String(Math.round(n * 100) / 100);
};

const KmInput: React.FC<KmInputProps> = ({
  id,
  value,
  onChange,
  onBlur,
  disabled,
  invalid,
  locked,
  placeholder = '0',
  size = 'md',
  className,
  ...aria
}) => {
  const [display, setDisplay] = useState(value.replace('.', ','));

  // External change (route recomputed, prefill) → resync unless the text is
  // merely a different spelling of the same number.
  useEffect(() => {
    if (normalizeKm(display) !== value) setDisplay(value.replace('.', ','));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <InputRoot
      size={size}
      invalid={invalid}
      className={cn(locked && !disabled && 'bg-bg-weak-50', className)}
    >
      <Input
        id={id}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={display}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-label={aria['aria-label']}
        // min-w-0: a text input's intrinsic width (~20ch) otherwise wins over
        // flex-1 and overflows a narrow wrapper (the planner's leg row).
        className="min-w-0 w-full font-mono tabular-nums text-right"
        onChange={(e) => {
          const raw = e.target.value;
          if (!PARTIAL.test(raw)) return; // reject letters / a 3rd decimal
          setDisplay(raw);
          const norm = normalizeKm(raw);
          if (norm !== null) onChange(norm);
        }}
        onBlur={() => {
          const norm = normalizeKm(display);
          if (norm !== null) {
            setDisplay(norm.replace('.', ','));
            if (norm !== value) onChange(norm);
          }
          onBlur?.();
        }}
      />
      <InputAffix className="font-medium text-text-sub-600">km</InputAffix>
    </InputRoot>
  );
};

export default KmInput;
